import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  border: '1px solid #f1f5f9',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.875rem',
  borderBottom: '1px solid #f1f5f9',
  '&.MuiTableCell-head': {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: 600,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#f8fafc',
  },
  '&:hover': {
    backgroundColor: '#f1f5f9',
  },
}));

const PostDetailsRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  '& > td': {
    paddingBottom: 0,
    paddingTop: 0,
  },
}));

const InfluencersTable = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [openRows, setOpenRows] = useState({});

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/influencers/posting-data`);
      const data = await response.json();
      setInfluencers(data);
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredInfluencers = influencers.filter(influencer =>
    influencer.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortedInfluencers = React.useMemo(() => {
    const sorted = [...filteredInfluencers].sort((a, b) => {
      if (sortConfig.key === 'posts') {
        return sortConfig.direction === 'desc'
          ? b.posts.length - a.posts.length
          : a.posts.length - b.posts.length;
      }
      return sortConfig.direction === 'desc'
        ? b[sortConfig.key] - a[sortConfig.key]
        : a[sortConfig.key] - b[sortConfig.key];
    });
    return sorted;
  }, [filteredInfluencers, sortConfig]);

  const toggleRow = (name) => {
    setOpenRows(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: '#ffffff', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <ArrowBackIcon
          sx={{
            mr: 2,
            cursor: 'pointer',
            color: '#64748b',
            '&:hover': { color: '#0f172a' }
          }}
          onClick={() => navigate('/dashboard')}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#0f172a',
          }}
        >
          Influencer Analytics
        </Typography>
      </Box>

      <StyledPaper>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search influencers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Rank</StyledTableCell>
                <StyledTableCell>Influencer</StyledTableCell>
                <StyledTableCell
                  align="right"
                  onClick={() => handleSort('revenue')}
                  sx={{ cursor: 'pointer' }}
                >
                  Revenue (€)
                  {sortConfig.key === 'revenue' && (
                    sortConfig.direction === 'desc' ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  onClick={() => handleSort('newRevenue')}
                  sx={{ cursor: 'pointer' }}
                >
                  New Revenue (€)
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  onClick={() => handleSort('totalOrders')}
                  sx={{ cursor: 'pointer' }}
                >
                  Total Orders
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  onClick={() => handleSort('posts')}
                  sx={{ cursor: 'pointer' }}
                >
                  Posts
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  onClick={() => handleSort('aov')}
                  sx={{ cursor: 'pointer' }}
                >
                  AOV (€)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedInfluencers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((influencer, index) => (
                  <React.Fragment key={influencer.name}>
                    <StyledTableRow>
                      <StyledTableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(influencer.name)}
                        >
                          {openRows[influencer.name] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </StyledTableCell>
                      <StyledTableCell>{page * rowsPerPage + index + 1}</StyledTableCell>
                      <StyledTableCell>{influencer.name}</StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE').format(influencer.revenue)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE').format(influencer.newRevenue)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE').format(influencer.totalOrders)}
                      </StyledTableCell>
                      <StyledTableCell align="right">{influencer.posts.length}</StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(influencer.aov)}
                      </StyledTableCell>
                    </StyledTableRow>
                    <PostDetailsRow>
                      <StyledTableCell colSpan={8}>
                        <Collapse in={openRows[influencer.name]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              Post History
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <StyledTableCell>Date</StyledTableCell>
                                  <StyledTableCell align="right">Revenue (€)</StyledTableCell>
                                  <StyledTableCell align="right">New Revenue (€)</StyledTableCell>
                                  <StyledTableCell align="right">Orders</StyledTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {influencer.posts.map((post) => (
                                  <TableRow key={post.date}>
                                    <StyledTableCell>
                                      {new Date(post.date).toLocaleDateString()}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                      {new Intl.NumberFormat('de-DE').format(post.revenue)}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                      {new Intl.NumberFormat('de-DE').format(post.newRevenue)}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                      {new Intl.NumberFormat('de-DE').format(post.orders)}
                                    </StyledTableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </StyledTableCell>
                    </PostDetailsRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedInfluencers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledPaper>
    </Box>
  );
};

export default InfluencersTable;